import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  Calendar, 
  Clock, 
  CheckCircle, 
  X, 
  Users, 
  TrendingUp, 
  Headphones, 
  Building, 
  Heart,
  BarChart,
  Sparkles,
  ArrowRight,
  Lightbulb,
  AlertTriangle,
  Gift,
  DollarSign
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function RokotepisteAICaller() {
  const [showCalculator, setShowCalculator] = useState(false);
  const [showApology, setShowApology] = useState(false);
  
  // Updated pricing structure
  const setupFee = 3300;
  const monthlyRetainer = 1950;
  const monthlyCallsHandled = 800;
  const monthlySavings = 3200;
  const annualSavings = monthlySavings * 12;
  const firstYearCost = setupFee + (monthlyRetainer * 12);
  const roi = Math.round(((annualSavings - (monthlyRetainer * 12)) / setupFee) * 100);
  const paybackPeriod = Math.ceil((setupFee / (monthlySavings - monthlyRetainer)) * 30); // days

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Phone className="w-16 h-16 text-golden animate-pulse" />
              <Heart className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Rokote<span className="text-golden">piste</span> AI Caller
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized AI Caller Solution
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We've analyzed Rokotepiste's unique needs and developed a specialized AI Caller solution that addresses the specific challenges of healthcare practices. Our system is designed to handle sensitive calls with empathy while improving operational efficiency.
            </p>
          </div>
        </motion.div>

        {/* Key Challenges Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">Challenges Unique to Healthcare</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Users className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Staff Overwhelmed by Calls</h3>
              <p className="text-gray-300">
                Your staff must handle emotionally charged calls with care and empathy, which can be time-consuming and emotionally draining.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Clock className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Appointment Management</h3>
              <p className="text-gray-300">
                Managing appointments, rescheduling, and handling cancellations requires significant administrative time that could be better spent on patient care.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <TrendingUp className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Patient Acquisition & Retention</h3>
              <p className="text-gray-300">
                Missed calls and long wait times can result in potential patients seeking care elsewhere, especially when they're in vulnerable states.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Building className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Administrative Burden</h3>
              <p className="text-gray-300">
                Your clinical staff spends valuable time on administrative tasks rather than focusing on providing quality care to your patients.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
            <h3 className="text-xl font-bold mb-3 text-center">The Impact on Your Practice</h3>
            <p className="text-gray-300 text-center mb-4">
              Based on our analysis of Rokotepiste:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-golden">35%</p>
                <p className="text-sm text-gray-300">Staff Time on Calls</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">22%</p>
                <p className="text-sm text-gray-300">Missed Calls</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">€3,200</p>
                <p className="text-sm text-gray-300">Monthly Loss</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">18%</p>
                <p className="text-sm text-gray-300">Patient Dissatisfaction</p>
              </div>
            </div>
          </div>
        </MouseFollowEffect>

        {/* Solution Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">The <span className="text-golden">AI Caller</span> Solution</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <Headphones className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Specialized for Healthcare</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>AI-powered voice assistant with empathetic tone for sensitive calls</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Crisis detection with immediate escalation to staff when needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>HIPAA-compliant scheduling and information management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Handles insurance verification and appointment reminders</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Seamless integration with your EHR and practice management systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Provides new patient intake information collection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Detailed analytics on call types, peak times, and patient needs</span>
                </li>
              </ul>
            </MouseFollowEffect>
            
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <BarChart className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Benefits for Rokotepiste</h3>
              <div className="space-y-4">
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">Enhanced Patient Experience</h4>
                  <p className="text-gray-300 text-sm">Every call answered promptly with empathy, creating a positive first impression for vulnerable patients</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">35% Staff Time Reclaimed</h4>
                  <p className="text-gray-300 text-sm">Your healthcare professionals can focus on treatment instead of administrative calls</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">24/7 Availability</h4>
                  <p className="text-gray-300 text-sm">Patients can schedule appointments even outside your regular business hours</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">25% Increase in New Patients</h4>
                  <p className="text-gray-300 text-sm">By capturing all calls and providing immediate assistance, more potential patients convert to appointments</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">Reduced No-Shows</h4>
                  <p className="text-gray-300 text-sm">Automated appointment reminders and easy rescheduling reduce missed appointments by up to 30%</p>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setShowCalculator(true)}
                  className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
                >
                  Calculate Your ROI <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Implementation Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">Seamless <span className="text-golden">Implementation</span></h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">1</span>
                  </div>
                  <h3 className="font-bold mb-2">Rapid Setup</h3>
                  <p className="text-gray-300 text-sm">
                    Two-day implementation with minimal disruption to your practice
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">2</span>
                  </div>
                  <h3 className="font-bold mb-2">Team Training</h3>
                  <p className="text-gray-300 text-sm">
                    Comprehensive 1-hour training for your clinical and administrative staff
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">3</span>
                  </div>
                  <h3 className="font-bold mb-2">Continuous Support</h3>
                  <p className="text-gray-300 text-sm">
                    Ongoing optimization and dedicated support for your practice
                  </p>
                </div>
              </div>
              
              <div className="mt-8 bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
                <h3 className="text-xl font-bold mb-3 text-center">Implementation Timeline</h3>
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 1</p>
                    <p className="text-sm text-gray-300">System setup & integration</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 2</p>
                    <p className="text-sm text-gray-300">Staff training & testing</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 3</p>
                    <p className="text-sm text-gray-300">Full deployment & optimization</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Ongoing</p>
                    <p className="text-sm text-gray-300">Maintenance & support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Your <span className="text-golden">Investment</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">AI Caller system implementation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Customization for healthcare needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">HIPAA-compliant security measures</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Integration with your existing systems</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Staff training and onboarding</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing technical support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Analytics dashboard for practice insights</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Special Multi-Clinic Offer</h3>
                  <div className="bg-golden/20 p-4 rounded-lg border border-golden/30 mb-4">
                    <p className="text-center font-bold text-white">
                      Implement in Multiple Clinics
                    </p>
                    <p className="text-center text-sm text-gray-300">
                      Get one implementation FREE with no retainer fees
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Implementation fee per clinic:</span>
                      <span className="text-golden font-bold">€{setupFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Monthly maintenance per agent:</span>
                      <span className="text-golden font-bold">€{monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <p className="text-center text-gray-300 text-sm mt-4">
                      We'll start with your clinic that receives the most inbound calls
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Next Steps</h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/rokotepiste-ai-caller-implementation"
                  className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Implementation Call
                </a>
                <button
                  onClick={() => setShowCalculator(true)}
                  className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
                >
                  View ROI Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* ROI Calculator Popup */}
      <AnimatePresence>
        {showCalculator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowCalculator(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-6 max-w-xl w-full border border-golden/30 m-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">ROI Analysis</h2>
                <button
                  onClick={() => setShowCalculator(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Initial Investment</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Setup Fee:</span>
                      <span className="font-bold">€{setupFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Monthly Maintenance:</span>
                      <span className="font-bold">€{monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">First Year Cost:</span>
                      <span className="font-bold">€{firstYearCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Monthly Benefits</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Calls Handled:</span>
                      <span className="font-bold">{monthlyCallsHandled.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Gross Savings:</span>
                      <span className="font-bold">€{monthlySavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Maintenance Cost:</span>
                      <span className="font-bold">-€{monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">Net Monthly Benefit:</span>
                      <span className="font-bold">€{(monthlySavings - monthlyRetainer).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Annual Benefits</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Gross Annual Savings:</span>
                      <span className="font-bold">€{annualSavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Annual Maintenance:</span>
                      <span className="font-bold">-€{(monthlyRetainer * 12).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">Net Annual Benefit:</span>
                      <span className="font-bold">€{(annualSavings - (monthlyRetainer * 12)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">ROI Metrics</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Return on Investment:</span>
                      <span className="font-bold">{roi}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Payback Period:</span>
                      <span className="font-bold">{paybackPeriod} Days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">5-Year Net Benefit:</span>
                      <span className="font-bold">€{((annualSavings - (monthlyRetainer * 12)) * 5 - setupFee).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-golden/20 to-black/30 p-3 rounded-xl border border-golden/30 mb-4">
                <div className="flex items-start">
                  <Lightbulb className="w-5 h-5 text-golden mr-2 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-sm">Multi-Clinic Benefits</h3>
                    <p className="text-xs text-gray-300">
                      By implementing in multiple clinics, you'll receive one implementation completely free with no retainer fees. This significantly improves your ROI and accelerates the payback period across your entire organization.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/rokotepiste-ai-caller-implementation"
                  className="inline-flex items-center px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Implementation Call
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}